import { useCallback, useEffect, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, MenuItem, Stack, TextField, Typography,
} from "@mui/material";
import {
  createWorkspaceMember, getWorkspaceMembers, updateWorkspaceMemberRole,
  updateWorkspaceMemberStatus,
} from "../api/workspaceMembers";
import { getApiError } from "../api/errors";
import { useAuth } from "../context/AuthContext";
import MainLayout from "../layouts/MainLayout";
import { useTranslation } from "react-i18next";

const emptyForm = { username: "", email: "", role: "staff" };

export default function WorkspaceMembers() {
  const { t } = useTranslation();
  const { selectedWorkspace, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const activeOwners = members.filter((member) => member.role === "owner" && member.is_active).length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getWorkspaceMembers();
      setMembers(response.data);
      setError("");
    } catch (requestError) {
      setError(getApiError(requestError, t("team.loadError")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load, selectedWorkspace?.public_id]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await createWorkspaceMember(form);
      setForm(emptyForm);
      setNotice(t("team.created"));
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("team.createError")));
    } finally {
      setBusy(false);
    }
  };

  const change = async (member, action) => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      if (action === "role") {
        await updateWorkspaceMemberRole(member.user_public_id, member.role === "admin" ? "staff" : "admin");
      } else {
        await updateWorkspaceMemberStatus(member.user_public_id, !member.is_active);
      }
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, t("team.updateError")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <MainLayout>
      <Typography variant="h4" fontWeight={900} mb={1}>{t("nav.team")}</Typography>
      <Typography color="text.secondary" mb={3}>{t("team.subtitle", { workspace: selectedWorkspace?.name || t("team.thisWorkspace") })}</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }}>{notice}</Alert>}
      <Card sx={{ mb: 3 }}><CardContent>
        <Typography variant="h6" fontWeight={800} mb={2}>{t("team.invite")}</Typography>
        <Box component="form" onSubmit={submit} sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField label={t("team.username")} value={form.username} required slotProps={{ htmlInput: { minLength: 3, maxLength: 50 } }}
            onChange={(event) => setForm({ ...form, username: event.target.value })} />
          <TextField label={t("team.email")} type="email" value={form.email} required
            onChange={(event) => setForm({ ...form, email: event.target.value })} />
          <TextField label={t("team.role")} select value={form.role} sx={{ minWidth: 130 }}
            onChange={(event) => setForm({ ...form, role: event.target.value })}>
            <MenuItem value="staff">{t("team.staff")}</MenuItem><MenuItem value="admin">{t("team.admin")}</MenuItem>
          </TextField>
          <Button type="submit" variant="contained" disabled={busy}>{t("team.create")}</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={2}>{t("team.passwordHelp")}</Typography>
      </CardContent></Card>
      {loading ? <Typography>{t("team.loading")}</Typography> : (
        <Stack spacing={1.5}>
          {members.map((member) => (
            <Card key={member.user_public_id}><CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography fontWeight={800}>{member.username} — {t(`team.${member.role}`, { defaultValue: member.role })}</Typography>
                <Typography variant="body2" color="text.secondary">{member.email} · {t(member.is_active ? "common.active" : "common.inactive")}</Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {member.role !== "owner" && (
                  <Button variant="outlined" disabled={busy} onClick={() => change(member, "role")}>{t("team.make", { role: t(member.role === "admin" ? "team.staff" : "team.admin") })}</Button>
                )}
                {member.role === "owner" && member.is_active && (member.user_public_id === user?.public_id || activeOwners <= 1) ? (
                  <Typography variant="caption" color="text.secondary">{t(member.user_public_id === user?.public_id ? "team.ownerSelfProtected" : "team.lastOwnerProtected")}</Typography>
                ) : <Button color={member.is_active ? "warning" : "success"} variant="outlined" disabled={busy}
                  onClick={() => change(member, "status")}>{t(member.is_active ? "team.deactivate" : "team.reactivate")}</Button>}
              </Stack>
            </CardContent></Card>
          ))}
        </Stack>
      )}
    </MainLayout>
  );
}
